import type { StoreResolvedCollectionItem } from '#imports';
import type { WireAuthProviderAdminRecord, WireAuthProviderListEntry } from '#layers/access';
import type { ResponseEnvelope } from '#layers/base/types/envelope';
import { $fetch } from 'ofetch';
import { joinURL } from 'ufo';
import { computed } from 'vue';
import * as z from 'zod/v4-mini';
import { useRuntimeConfig } from '#app/nuxt';
import { defineRstoreModule } from '#imports';
import { getErrorStatusCode } from '#layers/base';
import { useSystemFetch } from '~/composables/use-api';
import { redirect } from '~/composables/use-auth-transition';
import { useLastLoginMethod } from '~/composables/use-last-login-method';
import { useSsoReturnTo } from '~/composables/use-sso-return-to';
import { AUTH_ROUTES, EMAIL_LOGIN_METHOD } from '~/constants/auth';
import { FETCH_TIMEOUT_MS } from '~/constants/fetch';
import { useSystemInfo } from '~/stores/system-info';
import { CURRENT_USER_FIELDS_QUERY } from './auth/current-user-fields';

const PROVIDERS_PATH = 'providers';

const LoginResult = z.object({
	expires: z.number(),
});

const RefreshAccessTokenResult = z.object({
	expires: z.number(),
});

type LoginResult = z.infer<typeof LoginResult>;
type RefreshAccessTokenResult = z.infer<typeof RefreshAccessTokenResult>;

export const useAuth = defineRstoreModule('monospace-studio-auth', ({ store, defineState, defineMutation, onResolve }) => {
	const { refresh: refreshSystemInfo } = useSystemInfo();
	const state = defineState({
		loggedIn: false,
		currentUserKey: null as string | null,
	});

	const currentUser = store.MonospaceUser.query((q) => q.first(state.currentUserKey
		? {
				key: state.currentUserKey,
				// Surfaces the org role on the resolved type so `isOrganizationAdmin` can read
				// `isAdmin`. The eager load is guaranteed by `fetchCurrentUser`'s awaited `/me`
				// fetch (resolved before route gates run); this include keeps it on the query shape.
				include: { orgRole: { role: true } },
			}
		: {
				enabled: false,
			}));

	// Mirrors the engine's `ensure_org_entitlements` check (an org role with `isAdmin`),
	// which gates org-level operations like project create/update/delete and org settings.
	// Resolves false while the user or role is still unloaded.
	const isOrganizationAdmin = computed(() => Boolean(currentUser.data.value?.orgRole?.role?.isAdmin));

	const apiEndpoint = useRuntimeConfig().public.api;
	const authFetch = $fetch.create({ baseURL: joinURL(apiEndpoint, 'auth'), timeout: FETCH_TIMEOUT_MS });
	const systemFetch = useSystemFetch();

	let accessTokenExpiresIn = 0;
	let refreshAccessTokenTimeout: ReturnType<typeof setTimeout> | undefined;

	function setAccessTokenExpiresIn(expiresIn: number) {
		accessTokenExpiresIn = expiresIn;

		clearTimeout(refreshAccessTokenTimeout);

		refreshAccessTokenTimeout = setTimeout(() => {
			refreshAccessToken();
		}, accessTokenExpiresIn * 1000);
	}

	function loggedOut() {
		state.loggedIn = false;
		state.currentUserKey = null;
		accessTokenExpiresIn = 0;
		clearTimeout(refreshAccessTokenTimeout);

		// Drop any in-flight SSO context so a logout on a shared machine doesn't
		// leak the previous user's invite token / return-to URLs into the next
		// session in this tab.
		useSsoReturnTo().clear();
		useLastLoginMethod().setPending(undefined);
	}

	async function refreshAccessToken() {
		try {
			const data = await authFetch<RefreshAccessTokenResult>('refresh', {
				method: 'POST',
				body: {},
			});

			const result = RefreshAccessTokenResult.parse(data);

			state.loggedIn = true;
			setAccessTokenExpiresIn(result.expires);
		}
		catch (error) {
			if (getErrorStatusCode(error) === 401) {
				loggedOut();
				return;
			}

			console.error('Error refreshing access token:', error);
		}
	}

	onResolve(refreshAccessToken);

	async function fetchCurrentUser() {
		if (!state.loggedIn) {
			return;
		}

		try {
			// Hydrate `authLinks.providerId` so `useAccountConnections` avoids a
			// duplicate `/me` round-trip; unwrap the BE's to-many data envelope.
			type MeWire = Omit<StoreResolvedCollectionItem<'MonospaceUser'>, 'authLinks'> & {
				authLinks?: { data: { providerId: string }[] };
			};
			const { data: raw } = await systemFetch<ResponseEnvelope<MeWire>>(`system/users/me?fields=${CURRENT_USER_FIELDS_QUERY}`);
			const result: StoreResolvedCollectionItem<'MonospaceUser'> = {
				...raw,
				authLinks: raw.authLinks?.data ?? [],
			};
			state.currentUserKey = result.id;
			store.MonospaceUser.writeItem(result);
		}
		catch (error) {
			// 401 here means the refresh succeeded (logged-in flipped to true)
			// but the session can't read /me — treat as logged-out rather than
			// leaving the UI in a zombie "logged-in but no user" state.
			if (getErrorStatusCode(error) === 401) {
				loggedOut();
				return;
			}

			console.error('Error fetching current user:', error);
		}
	}

	onResolve(fetchCurrentUser);

	const login = defineMutation(async (payload: { email: string; password: string }) => {
		try {
			const data = await authFetch<LoginResult>('providers/local/password/login', {
				method: 'POST',
				body: payload,
			});

			const result = LoginResult.parse(data);

			state.loggedIn = true;
			setAccessTokenExpiresIn(result.expires);
			await fetchCurrentUser();

			useLastLoginMethod().set(EMAIL_LOGIN_METHOD);
		}
		catch (error) {
			if (getErrorStatusCode(error) === 401) {
				throw new Error('$t:auth.login.error', { cause: error });
			}

			throw error;
		}
	});

	const bootstrap = defineMutation(async (payload: {
		fullName: string;
		email: string;
		password: string;
		acceptTerms: boolean;
		acceptMarketing: boolean;
	}) => {
		await systemFetch('system/bootstrap', {
			method: 'POST',
			body: payload,
		});

		// Re-probe before navigation so the next middleware run sees the
		// authoritative onboarding state, not stale "before-bootstrap" flags.
		await refreshSystemInfo();
	});

	const logout = defineMutation(async () => {
		if (!state.loggedIn) {
			return;
		}

		await authFetch('logout', {
			method: 'POST',
			body: {},
		});

		loggedOut();
		await refreshSystemInfo();
	});

	async function requestPasswordReset(email: string) {
		await authFetch('providers/local/password/reset/request', {
			method: 'POST',
			body: { email },
		});
	}

	async function verifyPasswordResetToken(token: string): Promise<{ email: string }> {
		return await authFetch('providers/local/password/reset/verify', {
			method: 'GET',
			query: { token },
		});
	}

	async function resetPassword(token: string, password: string) {
		await authFetch('providers/local/password/reset/confirm', {
			method: 'POST',
			body: { token, password },
		});
	}

	async function changePassword(currentPassword: string, newPassword: string) {
		await authFetch('providers/local/password/change', {
			method: 'POST',
			body: { currentPassword, newPassword },
		});
	}

	// Side-effecting: POSTs then hands off to the IdP. Auth is the documented
	// exception for stores doing redirects, since login moves the user across
	// origins. Browser page-unload cancels the pending refreshAccessTokenTimeout
	// automatically. Hands off with `replace` so the login page leaves the back
	// stack — the SSO round-trip (login → IdP → callback → app) lands the user in
	// the app with no auth page reachable via Back.
	async function startSsoLogin(
		provider: Pick<WireAuthProviderListEntry, 'apiName' | 'loginPath'>,
		returnTo?: string,
	): Promise<void> {
		const { setLoginReturnTo, setSignupContext } = useSsoReturnTo();
		setLoginReturnTo(returnTo);
		// Clear any stale signup marker so an abandoned signup attempt can't make
		// the shared `?auth=success` callback mistake this login for a signup.
		setSignupContext(undefined);

		const result = await authFetch<{ redirectUrl: string }>(
			joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), provider.loginPath),
			{ method: 'POST', body: { returnTo: AUTH_ROUTES.callback } },
		);

		if (!result.redirectUrl) throw new Error('SSO login response is missing redirectUrl');

		// BE doesn't include the provider apiName in the success redirect, so
		// carry it via sessionStorage. callback.vue promotes to last-used only
		// on `authStatus === 'success'`. Set right before redirecting so a failed
		// POST above leaves no stale pending method behind.
		useLastLoginMethod().setPending(provider.apiName);

		// No transition here: we're handing off to the IdP and the outcome isn't
		// known yet. The expand fires on confirmed success in `callback.vue`.
		redirect(result.redirectUrl, { replace: true });
	}

	// Side-effecting: POSTs then hands off to the IdP (see startSsoLogin).
	// `fullName` is required by the BE on `/oauth2/signup` since the IdP claim
	// may not carry it reliably across providers; FE collects it upfront.
	async function startSsoSignup(
		provider: Pick<WireAuthProviderListEntry, 'apiName' | 'signupPath'>,
		inviteToken: string,
		fullName: string,
	): Promise<void> {
		const { setSignupContext, setLoginReturnTo } = useSsoReturnTo();
		// Mark the signup flow so the shared `?auth=success` callback can tell it
		// apart from login; persist the invite token so a callback error can
		// route the user back to the invite page instead of generic login.
		setLoginReturnTo(undefined);
		setSignupContext({ inviteToken });

		useLastLoginMethod().setPending(provider.apiName);

		try {
			const result = await authFetch<{ redirectUrl: string }>(
				joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), provider.signupPath),
				{ method: 'POST', body: { inviteToken, fullName, returnTo: AUTH_ROUTES.callback } },
			);

			if (!result.redirectUrl) throw new Error('SSO signup response is missing redirectUrl');

			// No transition here: handing off to the IdP, outcome not yet known.
			// The expand fires on confirmed success in `callback.vue`. `replace`
			// (like startSsoLogin): invite-accept is an auth page, so drop it from
			// the back stack; a callback error still offers "back to signup".
			redirect(result.redirectUrl, { replace: true });
		}
		catch (error) {
			// Undo the carry-across so a retry doesn't ship a stale provider through callback,
			// and drop the signup context so a failed start doesn't strand the invite token.
			useLastLoginMethod().setPending(undefined);
			setSignupContext(undefined);
			throw error;
		}
	}

	// Side-effecting: POSTs then redirects via window.location.href. Unlike the
	// SSO login/signup legs this starts from an in-app page (not an auth page),
	// so it keeps `href` — the originating page should stay in the back stack.
	async function startLinkFlow(
		provider: Pick<WireAuthProviderAdminRecord, 'apiName' | 'protocol'>,
		returnTo?: string,
	): Promise<void> {
		const { setLinkReturnTo } = useSsoReturnTo();
		const target = returnTo ?? window.location.pathname;
		setLinkReturnTo(target);

		// Route through `/auth/callback` (same seam as login) so the FE can
		// translate `?link=error&code=X` into `?linkError=X` before bouncing
		// to the destination read from sessionStorage above.
		const result = await authFetch<{ redirectUrl: string }>(
			joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), 'link'),
			{ method: 'POST', body: { returnTo: AUTH_ROUTES.callback } },
		);

		if (!result.redirectUrl) throw new Error('SSO link response is missing redirectUrl');

		// Link flow runs from `account/access.vue` under the default layout, where
		// the expand animation never mounts — skip it to avoid a dead ~200ms delay.
		redirect(result.redirectUrl);
	}

	async function unlinkProvider(
		provider: Pick<WireAuthProviderAdminRecord, 'apiName' | 'protocol'>,
	): Promise<void> {
		await authFetch(
			joinURL(PROVIDERS_PATH, encodeURIComponent(provider.apiName), 'link'),
			{ method: 'DELETE' },
		);
	}

	return {
		currentUserQuery: currentUser,
		currentUser: currentUser.data,
		isOrganizationAdmin,
		login,
		logout,
		bootstrap,
		password: {
			requestReset: requestPasswordReset,
			verifyResetToken: verifyPasswordResetToken,
			reset: resetPassword,
			change: changePassword,
		},
		startSsoLogin,
		startSsoSignup,
		startLinkFlow,
		unlinkProvider,
		fetchCurrentUser,
		loggedIn: computed(() => state.loggedIn),
	};
});
